import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { displaySnackBar } from 'client/services'
import { useDeleteQuestion } from 'client/api'
import { useModalDialogContext, usePage, usePageStateContext } from 'client/hooks'

import {
  AppButton,
  AppGroup,
  AppHeader,
  AppModalDialog,
  AppParagraph,
} from 'client/ui-building-blocks'

type Props = {
  questionId: string
}

const DeleteQuestionModal = ({ questionId }: Props) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const { navigateTo, goToCleanRoute } = usePage()
  const { setListActiveQuestionId } = usePageStateContext()
  const { showDeleteQuestionModal, toggleDeleteQuestionModal } = useModalDialogContext()

  const { mutateAsync: deleteQuestion, isLoading: isDeleting } = useDeleteQuestion({
    onSuccess: data => {
      if (data?.msg) {
        displaySnackBar(data.msg)
      }
      toggleDeleteQuestionModal(false)
      setListActiveQuestionId(null)
      if (pathname === '/question') {
        navigateTo({ path: '/browse' })
      } else {
        goToCleanRoute(true)
      }
    },
    onError: err => {
      setListActiveQuestionId(null)
      toggleDeleteQuestionModal(false)
      if (err?.msg) {
        displaySnackBar(err.msg)
      }
    },
  })

  if (!showDeleteQuestionModal) return null

  const onDelete = () => {
    deleteQuestion({
      questionId,
    })
  }

  const onCloseClick = () => {
    if (!queryClient.isFetching() && !queryClient.isMutating()) {
      toggleDeleteQuestionModal(false)
    }
  }

  return (
    <AppModalDialog
      size="tiny"
      open={showDeleteQuestionModal}
      onClose={onCloseClick}
      Header={
        <AppHeader
          text={t('app.modals.deleteQuestion.header')}
          iconProps={{ name: 'CLOSE', variant: 'error' }}
          dividing
        />
      }
      Content={<AppParagraph text={t('app.modals.deleteQuestion.description')} />}
      Actions={
        <AppGroup position="center" gapSize="xxsmall">
          <AppButton
            variant="error"
            text={t('common.buttons.delete')}
            loading={isDeleting}
            onClick={onDelete}
          />
          <AppButton
            variant="outlined"
            text={t('common.buttons.cancel')}
            disabled={isDeleting}
            onClick={onCloseClick}
          />
        </AppGroup>
      }
    />
  )
}

export default DeleteQuestionModal
